import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePWA } from '../hooks/usePWA';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import ForgotPassword from '../components/ForgotPassword';
import './Auth.css';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isBiometricLoading, setIsBiometricLoading] = useState(false);
  const [emailHasCredentials, setEmailHasCredentials] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login, loginWithBiometric } = useAuth();
  const { isPWA } = usePWA();
  const { isAvailable, hasCredentials, authenticateWithBiometric, checkEmailHasCredentials } = useBiometricAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Verificar credenciales cuando cambie el email
  useEffect(() => {
    const checkCredentials = async () => {
      if (formData.email.trim() && isPWA && isAvailable) {
        try {
          const hasCredentials = await checkEmailHasCredentials(formData.email);
          setEmailHasCredentials(hasCredentials);
        } catch (error) {
          setEmailHasCredentials(false);
        }
      } else {
        setEmailHasCredentials(false);
      }
    };

    // Debounce para no hacer muchas llamadas
    const timeoutId = setTimeout(checkCredentials, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.email, isPWA, isAvailable, checkEmailHasCredentials]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validación manual
    if (!formData.email.trim()) {
      setError('Introduce tu email');
      return;
    }
    
    if (!formData.password.trim()) {
      setError('Introduce tu contraseña');
      return;
    }
    
    setError('');
    setIsLoading(true);

    try {
      await login(formData);
      // Redireccionar al URL original o al dashboard por defecto
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err: any) {
      // Mostrar mensajes user-friendly en lugar de errores técnicos
      if (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.message?.includes('Invalid credentials')) {
        setError('Email o contraseña incorrectos');
      } else if (err.message?.includes('token') || err.message?.includes('Token')) {
        setError('Email o contraseña incorrectos');
      } else {
        setError(`${err.message || 'Error al iniciar sesión'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (!formData.email.trim()) {
      setError('Introduce tu email para usar autenticación biométrica');
      return;
    }

    setError('');
    setIsBiometricLoading(true);

    try {
      const { user, token } = await authenticateWithBiometric(formData.email);
      await loginWithBiometric(token, user);
      // Redireccionar al URL original o al dashboard por defecto
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsBiometricLoading(false);
    }
  };

  // Show forgot password component
  if (showForgotPassword) {
    return (
      <ForgotPassword 
        onBack={() => setShowForgotPassword(false)}
        initialEmail={formData.email}
      />
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon">🔐</span>
          <h2>Bienvenido de vuelta</h2>
          <p>Inicia sesión para acceder a tu dashboard personal</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}


        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="tu@email.com"
              autoComplete="username"
            />
          </div>


          {/* Botón biométrico solo en PWA */}
          {isPWA && isAvailable && emailHasCredentials && formData.email.trim() && (
            <div className="biometric-login-section">
              <button
                type="button"
                className="biometric-login-button"
                onClick={handleBiometricLogin}
                disabled={isBiometricLoading || isLoading}
              >
                {isBiometricLoading ? (
                  <>
                    <div className="spinner" style={{ width: 16, height: 16, margin: 0 }}></div>
                    Autenticando...
                  </>
                ) : (
                  <>
                    🔐 Entrar con Touch ID / Face ID
                  </>
                )}
              </button>
              
              <div className="biometric-divider">
                <span>O introduce tu contraseña</span>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Tu contraseña"
              autoComplete="current-password"
            />
            <div className="forgot-password-link">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="link-button"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner" style={{ width: 20, height: 20, margin: 0 }}></div>
                Iniciando...
              </>
            ) : (
              <>
                🚀 Iniciar Sesión
              </>
            )}
          </button>
        </form>

        <div className="oauth-divider">
          <span>O continúa con</span>
        </div>

        <button
          onClick={() => window.location.href = `${process.env.REACT_APP_API_URL || 'https://maldolor.com/api'}/auth/google`}
          className="google-auth-button"
          disabled={isLoading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" className="google-icon">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuar con Google
        </button>

        <div className="auth-footer">
          <p>
            ¿No tienes una cuenta?{' '}
            <Link to="/register" className="auth-link">
              Regístrate aquí
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;